/* Thin wrapper over the browser Web Speech API (speech-to-text + text-to-speech).
   Works with no API key. Used by the AI voice-ordering assistant. */

/* ---- minimal ambient types for the non-standard Web Speech API ---- */
interface SRAlternative { transcript: string; }
interface SRResult { 0: SRAlternative; isFinal: boolean; length: number; }
interface SRResultList { length: number; [i: number]: SRResult; }
interface SREvent extends Event { resultIndex: number; results: SRResultList; }
interface SRErrorEvent extends Event { error: string; }
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SREvent) => void) | null;
  onerror: ((e: SRErrorEvent) => void) | null;
  onend: (() => void) | null;
}
type SRConstructor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SRConstructor | null {
  const w = window as unknown as { SpeechRecognition?: SRConstructor; webkitSpeechRecognition?: SRConstructor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function speechSupported(): boolean {
  return getRecognitionCtor() !== null && typeof window.speechSynthesis !== 'undefined';
}

export class VoiceEngine {
  private rec: SpeechRecognitionLike | null = null;
  private listening = false;
  lang = 'en-IN';

  get isListening() { return this.listening; }

  /** Start one listen turn. onFinal fires once with the settled transcript. */
  listen(opts: {
    onPartial?: (text: string) => void;
    onFinal: (text: string) => void;
    onError?: (err: string) => void;
  }): void {
    const Ctor = getRecognitionCtor();
    if (!Ctor) { opts.onError?.('not-supported'); return; }
    this.stop();
    const rec = new Ctor();
    rec.lang = this.lang;
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    let finalText = '';
    rec.onresult = (e: SREvent) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (interim) opts.onPartial?.(interim);
    };
    rec.onerror = (e: SRErrorEvent) => { this.listening = false; opts.onError?.(e.error); };
    rec.onend = () => {
      this.listening = false;
      opts.onFinal(finalText.trim());
    };
    this.rec = rec;
    this.listening = true;
    try { rec.start(); } catch { /* already started */ }
  }

  stop(): void {
    if (this.rec) { try { this.rec.stop(); } catch { /* noop */ } }
    this.listening = false;
  }

  /** Speak text aloud; resolves when finished (or immediately if unsupported). */
  speak(text: string): Promise<void> {
    return new Promise((resolve) => {
      if (typeof window.speechSynthesis === 'undefined') { resolve(); return; }
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = this.lang;
      u.rate = 1.02;
      u.pitch = 1;
      u.onend = () => resolve();
      u.onerror = () => resolve();
      window.speechSynthesis.speak(u);
    });
  }

  shutUp(): void {
    if (typeof window.speechSynthesis !== 'undefined') window.speechSynthesis.cancel();
  }
}
